import { useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  getAllOrganizationsAsync,
  getCurrentOrganizationAsync,
  getOrganizationByIdAsync,
  getSelfAsync,
} from "@/data/store/actions";
import { useAppDispatch } from "@/data/store/utils/redux";

export const useDataFetching = () => {
  const dispatch = useAppDispatch();

  const fetchEssentialData = useCallback(
    async (authUser: any) => {
      if (!authUser) return { success: false };

      try {
        await dispatch(getSelfAsync()).unwrap();
        await dispatch(getAllOrganizationsAsync()).unwrap();
        return { success: true };
      } catch (error) {
        console.error("Failed to fetch essential user data:", error);
        return { success: false, error };
      }
    },
    [dispatch]
  );

  const fetchOrganizationData = useCallback(async () => {
    try {
      await dispatch(getSelfAsync()).unwrap();

      await dispatch(getCurrentOrganizationAsync({})).unwrap();

      return { success: true };
    } catch (error) {
      console.error("Failed to fetch organization data:", error);
      return { success: false, error };
    }
  }, [dispatch]);

  const refetchUserData = useCallback(
    async (authUser: any, organizationId: string | null, organization: any) => {
      if (!authUser) return;

      try {
        // Fetch essential data first
        const essentialResult = await fetchEssentialData(authUser);
        if (!essentialResult.success) {
          throw essentialResult.error;
        }

        // If user has an organization, fetch organization-specific data
        if (organizationId) {
          const orgResult = await fetchOrganizationData();
          if (!orgResult.success) {
            throw orgResult.error;
          }
        }
      } catch (error) {
        console.error("AuthProvider: Failed to refetch user data.", error);
        throw error;
      }
    },
    [fetchEssentialData, fetchOrganizationData]
  );

  return {
    fetchEssentialData,
    fetchOrganizationData,
    refetchUserData,
  };
};
